// ============================================================
// EXPOSURE HAZARDS - what touching an exposed target costs the one
// who touched it (v8.353.0): Spined, Burning, Grasping.
//
//   retaliate=<dice><type>[!ap]     melee attackers who HIT take it
//   retaliateMiss=<dice><type>[!ap] melee attackers who MISS take it
//   grasp=<S|Ag>[±N]                melee attackers who hit test, or are Snared
//
// The only exposure family that writes the ATTACKER's state, which the
// attack hook cannot do - it does not know who is swinging. So this
// module RESOLVES (rolls, soaks, decides) and returns what happened, and
// the composition root applies it to the actor the way Overheats hands
// its drop-or-hold to a prompt. Pure; dice through engine/dice.
//
// Soak follows the turn effects' rule (fire, v8.183.0): Toughness × the
// unnatural multiplier plus daemonic protection, minus nothing for
// armour - a spine in the palm does not care about the pauldron. `!ap`
// is kept in the grammar for symmetry and means "ignores TB as well",
// the Toxic convention.
// ============================================================
import { rollDiceExpression } from './dice';
import { rollPlainTargetTest, type TestResult } from './characteristicTests';
import { activeExposures, axisExprs, type Exposure, type ExposureBearer, type ExposureReader } from './exposures';
import type { Target } from '../types';

export interface HazardOutcome {
  /** Damage the attacker takes, after soak, summed over every hazard. */
  damage: number;
  /** Conditions the attacker gains - `Sn` from a failed grasp. */
  conditions: string[];
  /** One line per hazard, ready for the log. */
  notes: string[];
  /** The grasp tests rolled, for the structured log. */
  tests: TestResult[];
}

const NONE: HazardOutcome = { damage: 0, conditions: [], notes: [], tests: [] };

/** `1d5R`, `1d10E!ap` → the dice, the type letter, whether it ignores soak. */
export function parseRetaliation(expr: string): { dice: string; type: string; ignoresTB: boolean } | null {
  const m = expr.trim().match(/^(\d*d\d+(?:[+-]\d+)?|\d+)([EIRX])(!ap)?$/i);
  if (!m) return null;
  return { dice: m[1], type: m[2].toUpperCase(), ignoresTB: !!m[3] };
}

/** `S`, `Ag-10`, `S+0` → which test, at what modifier. */
export function parseGrasp(expr: string): { test: 'strength' | 'dodge'; label: string; modifier: number } | null {
  const m = expr.trim().match(/^(S|Ag)([+-]\d+)?$/i);
  if (!m) return null;
  const isS = m[1].toUpperCase() === 'S';
  return {
    test: isS ? 'strength' : 'dodge',
    label: isS ? 'Strength' : 'Agility',
    modifier: m[2] ? parseInt(m[2], 10) : 0,
  };
}

function soak(attacker: Target, ignoresTB: boolean): number {
  if (ignoresTB) return 0;
  return (attacker.toughnessBonus || 0) * (attacker.unnaturalToughness || 1) + (attacker.daemonicProtection || 0);
}

/** One retaliation, rolled and soaked: the damage it deals and its log line; null when malformed. */
function retaliation(name: string, expr: string, attacker: Target): { damage: number; note: string } | null {
  const r = parseRetaliation(expr);
  if (!r) return null;
  const rolled = rollDiceExpression(r.dice).total;
  const tb = soak(attacker, r.ignoresTB);
  const damage = Math.max(0, rolled - tb);
  const soakNote = r.ignoresTB ? ', ignores TB' : ` − ${tb} TB`;
  const note =
    damage > 0
      ? `⚠ ${name}: the attacker takes ${rolled}${soakNote} = ${damage} ${r.type} damage`
      : `⚠ ${name}: ${rolled}${soakNote} - the attacker shrugs it off`;
  return { damage, note };
}

/** Every retaliation for this outcome - hazards on a hit and on a miss are different axes. */
function applyRetaliations(active: Exposure[], attacker: Target, outcome: 'hit' | 'miss', out: HazardOutcome): void {
  for (const { name, expr } of axisExprs(active, outcome === 'hit' ? 'retaliate' : 'retaliateMiss')) {
    const r = retaliation(name, expr, attacker);
    if (!r) continue;
    out.damage += r.damage;
    out.notes.push(r.note);
  }
}

/** One grasp: the attacker tests, and is Snared on a failure. */
function applyGrasp(name: string, expr: string, attacker: Target, out: HazardOutcome): void {
  const g = parseGrasp(expr);
  if (!g) return;
  const test = rollPlainTargetTest(attacker, g.test, 0, g.modifier);
  out.tests.push(test);
  const vs = `${g.label} test (${test.roll} vs ${test.target})`;
  if (test.success) {
    out.notes.push(`⚠ ${name}: Pass ${vs} - the attacker pulls free.`);
    return;
  }
  if (!out.conditions.includes('Sn')) out.conditions.push('Sn');
  out.notes.push(`⚠ ${name}: FAIL ${vs} - the attacker is SNARED.`);
}

/**
 * Resolve every hazard the bearer wears against the attacker who just
 * touched them. `outcome` is what the attack did: hazards on a hit and
 * on a miss are different axes. Ranged attacks touch nothing. Dice run
 * every retaliation first, then (on a hit) every grasp test.
 */
export function resolveHazards(
  bearer: ExposureBearer,
  attacker: Target,
  reader: ExposureReader,
  outcome: 'hit' | 'miss',
): HazardOutcome {
  if (!reader.isMelee) return NONE;
  const active = activeExposures(bearer, reader);
  if (!active.length) return NONE;
  const out: HazardOutcome = { damage: 0, conditions: [], notes: [], tests: [] };
  applyRetaliations(active, attacker, outcome, out);
  if (outcome === 'hit') {
    for (const { name, expr } of axisExprs(active, 'grasp')) applyGrasp(name, expr, attacker, out);
  }
  return out;
}
